// AI assisted development
import { Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from '../../../environments/environment';
import { IframeEmbedService } from './iframe-embed.service';
import { isAllowedUpshopEmbedParentOrigin } from './upshop-embed-parent-origin.util';

/**
 * Handles `postMessage` from the Upshop parent (Commerce 360 embed, same protocol as AppUI2).
 * Payload: `{ type: 'NAVIGATE_TO', url: '/orders/inspector' }` — path only, same origin as CC routes.
 *
 * Security: `event.origin` must be HTTPS `*.upshop.com`, `upshop360.com`, or `*.upshop360.com` (AppUI2-aligned),
 * or a localhost dev origin when `!environment.isProd` (see `upshop-embed-parent-origin.util`).
 *
 * Listeners attach only while `IframeEmbedService.isIframeEmbed` is true.
 */
@Injectable({
  providedIn: 'root',
})
export class ParentFrameNavigationService {
  private messageListenerAttached = false;

  constructor(
    private readonly router: Router,
    private readonly ngZone: NgZone,
    private readonly iframeEmbed: IframeEmbedService,
  ) {
    if (typeof window === 'undefined') {
      return;
    }
    this.iframeEmbed.iframeEmbed$.subscribe(() => this.syncMessageListener());
    this.syncMessageListener();
  }

  private syncMessageListener(): void {
    if (typeof window === 'undefined') {
      return;
    }
    const want = this.iframeEmbed.isIframeEmbed;
    if (want === this.messageListenerAttached) {
      return;
    }
    if (want) {
      window.addEventListener('message', this.onMessage);
      this.messageListenerAttached = true;
    } else {
      window.removeEventListener('message', this.onMessage);
      this.messageListenerAttached = false;
    }
  }

  private readonly onMessage = (event: MessageEvent): void => {
    if (!this.isAllowedOrigin(event.origin)) {
      return;
    }
    const data = event.data;
    if (!data || typeof data !== 'object') {
      return;
    }
    if ((data as { type?: string }).type !== 'NAVIGATE_TO') {
      return;
    }
    const path = this.sanitizePath((data as { url?: unknown }).url);
    if (!path) {
      return;
    }
    /* postMessage runs outside Angular's zone; run navigation inside the zone so the router updates the view. */
    this.ngZone.run(() => {
      this.router.navigateByUrl(path).catch(() => {
        /* unknown route — ignore */
      });
    });
  };

  private isAllowedOrigin(origin: string): boolean {
    return isAllowedUpshopEmbedParentOrigin(origin, environment);
  }

  private sanitizePath(raw: unknown): string | null {
    if (typeof raw !== 'string') {
      return null;
    }
    if (raw.length > 2048) {
      return null;
    }
    const trimmed = raw.trim();
    if (!trimmed.startsWith('/') || trimmed.startsWith('//')) {
      return null;
    }
    if (trimmed.includes('://')) {
      return null;
    }
    if (/[\s<>"'`]/.test(trimmed)) {
      return null;
    }
    return trimmed;
  }
}
